import { ExternalLink, Search, FileCheck2, Download } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { ECRequestForm } from "../components/site/ECRequestForm";
import { Eyebrow, FadeUp } from "../components/site/Reveal";

const PORTAL = "https://registration.ec.ap.gov.in/ecSearch";

const STEPS = [
  { icon: Search, title: "Open the portal", text: "Go to the AP Registration & Stamps Department website and choose the Encumbrance Certificate service." },
  { icon: FileCheck2, title: "Enter property details", text: "Search by document number and year with the Sub-Registrar Office, or by the property's district, village and survey number." },
  { icon: Download, title: "Pay & download", text: "Pay the prescribed fee online and download the certificate directly." },
];

const SelfSearch = () => (
  <section className="paper py-20 sm:py-28" data-testid="ec-search-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Search EC online</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-6xl text-[#0A192F] leading-[1.02]">Check it yourself, <span className="italic text-[#8A6A2B]">in minutes.</span></h2>
        </div>
        <div className="lg:col-span-5 lg:text-right">
          <p className="text-[#2C3545]">Andhra Pradesh ECs are issued online through the official Registration & Stamps Department portal.</p>
          <a href={PORTAL} target="_blank" rel="noopener noreferrer" data-testid="ec-portal-link"
            className="mt-5 inline-flex items-center gap-3 rounded-full bg-[#0A192F] px-7 py-3.5 text-sm text-[#F4EEE3] transition-[background-color] duration-300 hover:bg-[#13284A]">
            Search EC on registration.ec.ap.gov.in <ExternalLink className="h-4 w-4 text-[#E3C88A]" />
          </a>
        </div>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <FadeUp key={s.title} delay={i * 0.08} className="rounded-[22px] border border-[#0A192F]/12 bg-white p-8" data-testid={`ec-step-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs tracking-[0.25em] text-[#8A6A2B]">STEP 0{i + 1}</span>
                <Icon className="h-6 w-6 text-[#8A6A2B]" strokeWidth={1.4} />
              </div>
              <h3 className="mt-8 font-display text-3xl text-[#0A192F]">{s.title}</h3>
              <p className="mt-3 text-[#4A5468] leading-relaxed">{s.text}</p>
            </FadeUp>
          );
        })}
      </div>
    </div>
  </section>
);

export default function EncumbranceCertificate() {
  return (
    <>
      <PageHero testid="ec-hero" eyebrow="Encumbrance Certificate (EC)" lines={["Know every claim", <span className="italic text-[#E3C88A]">on the land.</span>]}
        intro="An Encumbrance Certificate lists the registered transactions on a property over a period of time. Search it online yourself, or send us the details and we'll obtain and review it for you." />
      <SelfSearch />
      <section className="bg-[#EDE5D6] py-20 sm:py-28" data-testid="ec-request-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <Eyebrow>Request through us</Eyebrow>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl text-[#0A192F] leading-tight max-w-3xl">Share the property details. <span className="italic text-[#8A6A2B]">We'll handle the rest.</span></h2>
          <div className="mt-12 rounded-[28px] bg-white border border-[#0A192F]/10 p-7 sm:p-12"><ECRequestForm /></div>
        </div>
      </section>
    </>
  );
}
